import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import authgear from "../authgear";
import Header from "../components/Header";

const base_url = process.env.NODE_ENV === "production"
    ? "https://hooks-28xd.onrender.com"
    : "http://localhost:4000";

const ACCENT = "#00d4ff";
const GOOD = "#4ecdc4";

const card = {
    background: "#1a1a2e",
    border: "1px solid #2a2a4a",
    borderRadius: "10px",
    padding: "18px 20px",
};

const inputStyle = {
    padding: "8px 10px",
    background: "#0a0a14",
    border: "1px solid #2a2a4a",
    borderRadius: "6px",
    color: "#ddd",
    fontSize: "13px",
};

const buttonStyle = (disabled) => ({
    padding: "8px 16px",
    background: disabled ? "#0a1a2a" : "#0a2a3a",
    border: "1px solid #2a3a4a",
    borderRadius: "6px",
    color: disabled ? "#444" : ACCENT,
    cursor: disabled ? "not-allowed" : "pointer",
    fontSize: "13px",
    whiteSpace: "nowrap",
});

function todayString() {
    const now = new Date();
    const offsetMs = now.getTimezoneOffset() * 60000;
    return new Date(now.getTime() - offsetMs).toISOString().slice(0, 10);
}

function round1(n) {
    return Math.round((n || 0) * 10) / 10;
}

const CalorieTracker = () => {
    const navigate = useNavigate();
    const date = useMemo(() => todayString(), []);

    const [profileForm, setProfileForm] = useState({ heightInches: "", weightLbs: "", age: "", sex: "", activityLevel: "sedentary" });
    const [needs, setNeeds] = useState(null);
    const [needsMessage, setNeedsMessage] = useState("");
    const [quickFoods, setQuickFoods] = useState([]);
    const [entries, setEntries] = useState([]);
    const [workout, setWorkout] = useState({ activities: [], totalCalories: 0, usedFallbackWeight: false });

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [savingProfile, setSavingProfile] = useState(false);

    const [manualForm, setManualForm] = useState({ name: "", calories: "", protein: "", carbs: "", fat: "" });
    const [adding, setAdding] = useState(false);

    const [description, setDescription] = useState("");
    const [estimating, setEstimating] = useState(false);
    const [aiPreview, setAiPreview] = useState(null);
    const [aiError, setAiError] = useState("");

    const [quickAddingId, setQuickAddingId] = useState(null);
    const [showComparison, setShowComparison] = useState(false);

    const getToken = async () => {
        await authgear.refreshAccessTokenIfNeeded();
        return authgear.accessToken;
    };

    const loadAll = useCallback(async () => {
        setError(null);
        try {
            const token = await getToken();
            const headers = { Authorization: `Bearer ${token}` };

            const [profileRes, needsRes, quickFoodsRes, entriesRes, workoutRes] = await Promise.all([
                axios.get(`${base_url}/calories/profile`, { headers }),
                axios.get(`${base_url}/calories/needs`, { headers }),
                axios.get(`${base_url}/calories/quick-foods`, { headers }),
                axios.get(`${base_url}/calories/entries?date=${date}`, { headers }),
                axios.get(`${base_url}/calories/workout-estimate?date=${date}`, { headers }),
            ]);

            const p = profileRes.data.profile;
            setProfileForm({
                heightInches: p?.heightInches ?? "",
                weightLbs: p?.weightLbs ?? "",
                age: p?.age ?? "",
                sex: p?.sex ?? "",
                activityLevel: p?.activityLevel ?? "sedentary",
            });
            setNeeds(needsRes.data.needs);
            setNeedsMessage(needsRes.data.message || "");
            setQuickFoods(quickFoodsRes.data.quickFoods);
            setEntries(entriesRes.data.entries);
            setWorkout(workoutRes.data);
        } catch (err) {
            setError(err.response?.data?.message || err.message);
        } finally {
            setLoading(false);
        }
    }, [date]);

    useEffect(() => {
        if (authgear.sessionState !== "AUTHENTICATED") {
            navigate("/login");
            return;
        }
        loadAll();
    }, [navigate, loadAll]);

    const refreshEntriesAndNeeds = async () => {
        const token = await getToken();
        const headers = { Authorization: `Bearer ${token}` };
        const [entriesRes, needsRes] = await Promise.all([
            axios.get(`${base_url}/calories/entries?date=${date}`, { headers }),
            axios.get(`${base_url}/calories/needs`, { headers }),
        ]);
        setEntries(entriesRes.data.entries);
        setNeeds(needsRes.data.needs);
        setNeedsMessage(needsRes.data.message || "");
    };

    const handleSaveProfile = async (e) => {
        e.preventDefault();
        setSavingProfile(true);
        setError(null);
        try {
            const token = await getToken();
            const payload = {
                heightInches: profileForm.heightInches === "" ? undefined : Number(profileForm.heightInches),
                weightLbs: profileForm.weightLbs === "" ? undefined : Number(profileForm.weightLbs),
                age: profileForm.age === "" ? undefined : Number(profileForm.age),
                sex: profileForm.sex || undefined,
                activityLevel: profileForm.activityLevel || undefined,
            };
            await axios.put(`${base_url}/calories/profile`, payload, { headers: { Authorization: `Bearer ${token}` } });
            await refreshEntriesAndNeeds();
            const workoutRes = await axios.get(`${base_url}/calories/workout-estimate?date=${date}`, { headers: { Authorization: `Bearer ${token}` } });
            setWorkout(workoutRes.data);
        } catch (err) {
            setError(err.response?.data?.message || err.message);
        } finally {
            setSavingProfile(false);
        }
    };

    const handleQuickAdd = async (quickFoodId) => {
        setQuickAddingId(quickFoodId);
        setError(null);
        try {
            const token = await getToken();
            await axios.post(`${base_url}/calories/entries/quick-add`, { date, quickFoodId }, { headers: { Authorization: `Bearer ${token}` } });
            await refreshEntriesAndNeeds();
        } catch (err) {
            setError(err.response?.data?.message || err.message);
        } finally {
            setQuickAddingId(null);
        }
    };

    const handleManualAdd = async (e) => {
        e.preventDefault();
        if (!manualForm.name.trim() || manualForm.calories === "") return;
        setAdding(true);
        setError(null);
        try {
            const token = await getToken();
            await axios.post(`${base_url}/calories/entries`, {
                date,
                name: manualForm.name,
                calories: manualForm.calories,
                protein: manualForm.protein || 0,
                carbs: manualForm.carbs || 0,
                fat: manualForm.fat || 0,
                source: "manual",
            }, { headers: { Authorization: `Bearer ${token}` } });
            setManualForm({ name: "", calories: "", protein: "", carbs: "", fat: "" });
            await refreshEntriesAndNeeds();
        } catch (err) {
            setError(err.response?.data?.message || err.message);
        } finally {
            setAdding(false);
        }
    };

    const handleEstimate = async (e) => {
        e.preventDefault();
        if (!description.trim()) return;
        setEstimating(true);
        setAiError("");
        setAiPreview(null);
        try {
            const token = await getToken();
            const { data } = await axios.post(`${base_url}/calories/estimate`, { description }, { headers: { Authorization: `Bearer ${token}` } });
            if (data.success) {
                setAiPreview(data.estimate);
            } else {
                setAiError(data.message);
            }
        } catch (err) {
            setAiError(err.response?.data?.message || err.message);
        } finally {
            setEstimating(false);
        }
    };

    const handleConfirmAiEstimate = async () => {
        if (!aiPreview) return;
        setAdding(true);
        setError(null);
        try {
            const token = await getToken();
            await axios.post(`${base_url}/calories/entries`, { date, ...aiPreview, source: "ai" }, { headers: { Authorization: `Bearer ${token}` } });
            setAiPreview(null);
            setDescription("");
            await refreshEntriesAndNeeds();
        } catch (err) {
            setError(err.response?.data?.message || err.message);
        } finally {
            setAdding(false);
        }
    };

    const handleDeleteEntry = async (id) => {
        setError(null);
        try {
            const token = await getToken();
            await axios.delete(`${base_url}/calories/entries/${id}`, { headers: { Authorization: `Bearer ${token}` } });
            await refreshEntriesAndNeeds();
        } catch (err) {
            setError(err.response?.data?.message || err.message);
        }
    };

    const totals = useMemo(() => entries.reduce((acc, e) => ({
        calories: acc.calories + (e.calories || 0),
        protein: acc.protein + (e.protein || 0),
        carbs: acc.carbs + (e.carbs || 0),
        fat: acc.fat + (e.fat || 0),
    }), { calories: 0, protein: 0, carbs: 0, fat: 0 }), [entries]);

    const quickFoodCounts = useMemo(() => {
        const counts = {};
        entries.filter(e => e.source === "quick").forEach(e => {
            counts[e.name] = (counts[e.name] || 0) + 1;
        });
        return counts;
    }, [entries]);

    // Workout calories are added on top of the base target (which reflects
    // general daily activity level, not today's specific exercise), so
    // logging a hard workout raises today's calorie/carb allowance instead
    // of double counting it into the base TDEE.
    const adjustedTarget = needs ? {
        calories: needs.tdee + workout.totalCalories,
        protein: needs.protein,
        fat: needs.fat,
        carbs: needs.carbs + workout.totalCalories / 4,
    } : null;

    const calorieChartData = adjustedTarget ? [
        { name: "Calories", consumed: Math.round(totals.calories), target: Math.round(adjustedTarget.calories) },
    ] : [];
    const macroChartData = adjustedTarget ? [
        { name: "Protein (g)", consumed: round1(totals.protein), target: Math.round(adjustedTarget.protein) },
        { name: "Carbs (g)", consumed: round1(totals.carbs), target: Math.round(adjustedTarget.carbs) },
        { name: "Fat (g)", consumed: round1(totals.fat), target: Math.round(adjustedTarget.fat) },
    ] : [];

    return (
        <div style={{ minHeight: "100vh", background: "#0d0d1a", color: "#ddd", fontFamily: "Verdana, sans-serif" }}>
            <Header />
            <div style={{ padding: "60px 20px 40px", maxWidth: "900px", margin: "0 auto" }}>
                <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: "24px", flexWrap: "wrap", gap: "8px" }}>
                    <h2 style={{ color: ACCENT, margin: 0 }}>Calorie Tracker</h2>
                    <span style={{ fontSize: "12px", color: "#555" }}>{date}</span>
                </div>

                {error && (
                    <div style={{ background: "#2a1a1a", border: "1px solid #4a2a2a", borderRadius: "8px", padding: "12px 16px", color: "#f88", fontSize: "13px", marginBottom: "20px" }}>
                        Error: {error}
                    </div>
                )}

                {loading ? (
                    <p style={{ color: "#555", textAlign: "center", marginTop: "60px" }}>Loading...</p>
                ) : (
                    <>
                        {/* Profile: height/weight/age/sex/activity */}
                        <div style={{ ...card, marginBottom: "20px" }}>
                            <h3 style={{ margin: "0 0 14px", fontSize: "14px", color: "#aaa" }}>Profile</h3>
                            <form onSubmit={handleSaveProfile} style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: "10px", alignItems: "end" }}>
                                <label style={{ display: "flex", flexDirection: "column", gap: "4px", fontSize: "11px", color: "#888" }}>
                                    Height (in)
                                    <input type="number" step="0.1" style={inputStyle} value={profileForm.heightInches}
                                        onChange={e => setProfileForm(f => ({ ...f, heightInches: e.target.value }))} />
                                </label>
                                <label style={{ display: "flex", flexDirection: "column", gap: "4px", fontSize: "11px", color: "#888" }}>
                                    Weight (lbs)
                                    <input type="number" step="0.1" style={inputStyle} value={profileForm.weightLbs}
                                        onChange={e => setProfileForm(f => ({ ...f, weightLbs: e.target.value }))} />
                                </label>
                                <label style={{ display: "flex", flexDirection: "column", gap: "4px", fontSize: "11px", color: "#888" }}>
                                    Age
                                    <input type="number" style={inputStyle} value={profileForm.age}
                                        onChange={e => setProfileForm(f => ({ ...f, age: e.target.value }))} />
                                </label>
                                <label style={{ display: "flex", flexDirection: "column", gap: "4px", fontSize: "11px", color: "#888" }}>
                                    Sex
                                    <select style={inputStyle} value={profileForm.sex} onChange={e => setProfileForm(f => ({ ...f, sex: e.target.value }))}>
                                        <option value="">—</option>
                                        <option value="male">Male</option>
                                        <option value="female">Female</option>
                                    </select>
                                </label>
                                <label style={{ display: "flex", flexDirection: "column", gap: "4px", fontSize: "11px", color: "#888" }}>
                                    Activity level
                                    <select style={inputStyle} value={profileForm.activityLevel} onChange={e => setProfileForm(f => ({ ...f, activityLevel: e.target.value }))}>
                                        <option value="sedentary">Sedentary</option>
                                        <option value="light">Lightly active</option>
                                        <option value="moderate">Moderately active</option>
                                        <option value="active">Active</option>
                                        <option value="very_active">Very active</option>
                                    </select>
                                </label>
                                <button type="submit" disabled={savingProfile} style={buttonStyle(savingProfile)}>
                                    {savingProfile ? "Saving..." : "Save"}
                                </button>
                            </form>
                            {needsMessage && !needs && (
                                <p style={{ fontSize: "11px", color: "#666", marginTop: "10px", marginBottom: 0 }}>{needsMessage}</p>
                            )}
                        </div>

                        {/* Workout calories today */}
                        <div style={{ ...card, marginBottom: "20px" }}>
                            <h3 style={{ margin: "0 0 14px", fontSize: "14px", color: "#aaa" }}>Estimated calories burned today</h3>
                            {workout.activities.length === 0 ? (
                                <p style={{ color: "#555", fontSize: "13px", margin: 0 }}>No completed workouts logged for today.</p>
                            ) : (
                                <>
                                    <div style={{ display: "flex", flexDirection: "column", gap: "6px", marginBottom: "10px" }}>
                                        {workout.activities.map(a => (
                                            <div key={a.id} style={{ display: "flex", justifyContent: "space-between", fontSize: "12px", color: "#ccc" }}>
                                                <span>{a.name} <span style={{ color: "#666" }}>({a.type})</span></span>
                                                <span style={{ color: GOOD }}>{a.estimated ? "~" : ""}{a.calories} cal{a.estimated ? "" : " (Strava)"}</span>
                                            </div>
                                        ))}
                                    </div>
                                    <div style={{ borderTop: "1px solid #2a2a4a", paddingTop: "8px", display: "flex", justifyContent: "space-between", fontSize: "13px", fontWeight: "bold" }}>
                                        <span>Total</span>
                                        <span style={{ color: GOOD }}>~{workout.totalCalories} cal</span>
                                    </div>
                                    {workout.usedFallbackWeight && (
                                        <p style={{ fontSize: "11px", color: "#666", marginTop: "8px", marginBottom: 0 }}>
                                            Estimated using a default body weight — save your weight above for a more accurate estimate.
                                        </p>
                                    )}
                                </>
                            )}
                        </div>

                        {/* Quick add frequent foods */}
                        <div style={{ ...card, marginBottom: "20px" }}>
                            <h3 style={{ margin: "0 0 14px", fontSize: "14px", color: "#aaa" }}>Quick add</h3>
                            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: "10px" }}>
                                {quickFoods.map(f => (
                                    <button key={f.id} onClick={() => handleQuickAdd(f.id)} disabled={quickAddingId === f.id}
                                        style={{
                                            textAlign: "left",
                                            background: "#0a0a14",
                                            border: "1px solid #2a2a4a",
                                            borderRadius: "8px",
                                            padding: "10px 12px",
                                            cursor: quickAddingId === f.id ? "not-allowed" : "pointer",
                                            color: "#ddd",
                                        }}>
                                        <div style={{ fontSize: "12px", fontWeight: "bold" }}>{f.name}</div>
                                        <div style={{ fontSize: "10px", color: "#666", marginTop: "4px" }}>
                                            {f.calories} cal · P{f.protein} C{f.carbs} F{f.fat}
                                        </div>
                                        <div style={{ fontSize: "10px", color: ACCENT, marginTop: "6px" }}>
                                            {quickAddingId === f.id ? "Adding..." : `+ Add${quickFoodCounts[f.name] ? ` (eaten ${quickFoodCounts[f.name]}x today)` : ""}`}
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Manual entry */}
                        <div style={{ ...card, marginBottom: "20px" }}>
                            <h3 style={{ margin: "0 0 14px", fontSize: "14px", color: "#aaa" }}>Add a food</h3>
                            <form onSubmit={handleManualAdd} style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(110px, 1fr))", gap: "8px", alignItems: "end", marginBottom: "14px" }}>
                                <label style={{ display: "flex", flexDirection: "column", gap: "4px", fontSize: "11px", color: "#888", gridColumn: "span 2" }}>
                                    Name
                                    <input style={inputStyle} value={manualForm.name} onChange={e => setManualForm(f => ({ ...f, name: e.target.value }))} placeholder="Chicken sandwich" />
                                </label>
                                <label style={{ display: "flex", flexDirection: "column", gap: "4px", fontSize: "11px", color: "#888" }}>
                                    Calories
                                    <input type="number" style={inputStyle} value={manualForm.calories} onChange={e => setManualForm(f => ({ ...f, calories: e.target.value }))} />
                                </label>
                                <label style={{ display: "flex", flexDirection: "column", gap: "4px", fontSize: "11px", color: "#888" }}>
                                    Protein (g)
                                    <input type="number" style={inputStyle} value={manualForm.protein} onChange={e => setManualForm(f => ({ ...f, protein: e.target.value }))} />
                                </label>
                                <label style={{ display: "flex", flexDirection: "column", gap: "4px", fontSize: "11px", color: "#888" }}>
                                    Carbs (g)
                                    <input type="number" style={inputStyle} value={manualForm.carbs} onChange={e => setManualForm(f => ({ ...f, carbs: e.target.value }))} />
                                </label>
                                <label style={{ display: "flex", flexDirection: "column", gap: "4px", fontSize: "11px", color: "#888" }}>
                                    Fat (g)
                                    <input type="number" style={inputStyle} value={manualForm.fat} onChange={e => setManualForm(f => ({ ...f, fat: e.target.value }))} />
                                </label>
                                <button type="submit" disabled={adding} style={buttonStyle(adding)}>Add</button>
                            </form>

                            <div style={{ borderTop: "1px solid #2a2a4a", paddingTop: "14px" }}>
                                <p style={{ fontSize: "11px", color: "#888", margin: "0 0 8px" }}>Or describe what you ate and estimate with AI:</p>
                                <form onSubmit={handleEstimate} style={{ display: "flex", gap: "8px" }}>
                                    <input
                                        value={description}
                                        onChange={e => setDescription(e.target.value)}
                                        placeholder="2 slices of pepperoni pizza and a diet coke"
                                        style={{ ...inputStyle, flex: 1 }}
                                    />
                                    <button type="submit" disabled={estimating} style={buttonStyle(estimating)}>
                                        {estimating ? "Estimating..." : "Estimate with AI"}
                                    </button>
                                </form>
                                {aiError && <p style={{ color: "#f88", fontSize: "12px", marginTop: "8px" }}>{aiError}</p>}
                                {aiPreview && (
                                    <div style={{ marginTop: "10px", background: "#0a0a14", border: "1px solid #2a2a4a", borderRadius: "8px", padding: "10px 12px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "8px" }}>
                                        <div style={{ fontSize: "12px" }}>
                                            <strong>{aiPreview.name}</strong> — {Math.round(aiPreview.calories)} cal
                                            <span style={{ color: "#666" }}> (P{round1(aiPreview.protein)} C{round1(aiPreview.carbs)} F{round1(aiPreview.fat)})</span>
                                        </div>
                                        <div style={{ display: "flex", gap: "8px" }}>
                                            <button onClick={handleConfirmAiEstimate} disabled={adding} style={buttonStyle(adding)}>Add to log</button>
                                            <button onClick={() => setAiPreview(null)} style={{ ...buttonStyle(false), background: "transparent", color: "#888" }}>Discard</button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Today's log */}
                        <div style={{ ...card, marginBottom: "20px" }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: "14px" }}>
                                <h3 style={{ margin: 0, fontSize: "14px", color: "#aaa" }}>Today's food log</h3>
                                <span style={{ fontSize: "13px", fontWeight: "bold", color: ACCENT }}>{Math.round(totals.calories)} cal</span>
                            </div>
                            {entries.length === 0 ? (
                                <p style={{ color: "#555", fontSize: "13px" }}>Nothing logged yet today.</p>
                            ) : (
                                <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                                    {entries.map(e => (
                                        <div key={e._id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: "12px", borderBottom: "1px solid #1a1a2e", paddingBottom: "6px" }}>
                                            <span style={{ color: "#ddd" }}>{e.name}</span>
                                            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                                <span style={{ color: "#888" }}>{e.calories} cal · P{round1(e.protein)} C{round1(e.carbs)} F{round1(e.fat)}</span>
                                                <button onClick={() => handleDeleteEntry(e._id)} style={{ background: "transparent", border: "none", color: "#f87171", cursor: "pointer", fontSize: "12px" }}>✕</button>
                                            </div>
                                        </div>
                                    ))}
                                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: "12px", fontWeight: "bold", paddingTop: "4px" }}>
                                        <span>Total</span>
                                        <span>{Math.round(totals.calories)} cal · P{round1(totals.protein)} C{round1(totals.carbs)} F{round1(totals.fat)}</span>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Compare to needs */}
                        <div style={card}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: "14px", flexWrap: "wrap", gap: "8px" }}>
                                <h3 style={{ margin: 0, fontSize: "14px", color: "#aaa" }}>Compare to daily needs</h3>
                                <button onClick={() => setShowComparison(s => !s)} disabled={!needs} style={buttonStyle(!needs)}>
                                    {showComparison ? "Hide comparison" : "Compare"}
                                </button>
                            </div>
                            {!needs ? (
                                <p style={{ color: "#555", fontSize: "13px", margin: 0 }}>Complete your profile above to see estimated needs.</p>
                            ) : showComparison && (
                                <>
                                    <p style={{ fontSize: "11px", color: "#666", margin: "0 0 14px" }}>
                                        Target includes your base daily need (BMR {needs.bmr} × activity level) plus ~{workout.totalCalories} cal for today's completed workouts.
                                    </p>
                                    <ResponsiveContainer width="100%" height={120}>
                                        <BarChart data={calorieChartData} margin={{ left: 8, right: 24, top: 4, bottom: 4 }}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#2a2a4a" />
                                            <XAxis dataKey="name" tick={{ fill: "#888", fontSize: 11 }} axisLine={{ stroke: "#2a2a4a" }} tickLine={false} />
                                            <YAxis tick={{ fill: "#888", fontSize: 11 }} axisLine={{ stroke: "#2a2a4a" }} tickLine={false} />
                                            <Tooltip contentStyle={{ background: "#0a0a14", border: "1px solid #2a2a4a", borderRadius: "6px", fontSize: "12px" }} />
                                            <Legend wrapperStyle={{ fontSize: "11px" }} />
                                            <Bar dataKey="consumed" name="Consumed" fill={ACCENT} radius={[4, 4, 0, 0]} />
                                            <Bar dataKey="target" name="Target" fill="#555" radius={[4, 4, 0, 0]} />
                                        </BarChart>
                                    </ResponsiveContainer>
                                    <ResponsiveContainer width="100%" height={180}>
                                        <BarChart data={macroChartData} margin={{ left: 8, right: 24, top: 16, bottom: 4 }}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#2a2a4a" />
                                            <XAxis dataKey="name" tick={{ fill: "#888", fontSize: 11 }} axisLine={{ stroke: "#2a2a4a" }} tickLine={false} />
                                            <YAxis tick={{ fill: "#888", fontSize: 11 }} axisLine={{ stroke: "#2a2a4a" }} tickLine={false} />
                                            <Tooltip contentStyle={{ background: "#0a0a14", border: "1px solid #2a2a4a", borderRadius: "6px", fontSize: "12px" }} />
                                            <Legend wrapperStyle={{ fontSize: "11px" }} />
                                            <Bar dataKey="consumed" name="Consumed" fill={GOOD} radius={[4, 4, 0, 0]} />
                                            <Bar dataKey="target" name="Target" fill="#555" radius={[4, 4, 0, 0]} />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default CalorieTracker;
